import dayjs from 'dayjs';
import 'dayjs/locale/zh';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

// Docs: https://airwallex.atlassian.net/wiki/spaces/FRONT/pages/4142206218/Date+time+format
// Rules (default timezone: SGT / GMT+8, default precision: minute):
// - Natural (prose):
//   • date-only: 31 Jan 2025
//   • date-time-minute: 31 Jan 2025 at 3:45 PM SGT
//   • date-time-second: 31 Jan 2025 at 3:45:55 PM SGT
// - Numeric (tables):
//   • date-only: 2025-01-31
//   • date-time-minute: 2025-07-31, 04:11 PM GMT+8
//   • date-time-second: 2025-07-31, 04:11:05 PM GMT+8
// Defaults when missing: timezone → SGT / GMT+8, AM/PM → PM.

export enum DatePrecision {
  DATE_ONLY = 'date-only',
  DATE_TIME_MINUTE = 'date-time-minute',
  DATE_TIME_SECOND = 'date-time-second',
}

export enum DateStyle {
  NATURAL = 'natural',
  NUMERIC = 'numeric',
}

// Export for backwards compatibility with component usage
export const DATE_PRECISION = DatePrecision;
export const DATE_STYLE = DateStyle;

export type DatePrecisionType = `${DatePrecision}`;
export type DateStyleType = `${DateStyle}`;

const DEFAULT_TIMEZONE = 'Asia/Singapore';
const NATURAL_TZ_LABEL = 'SGT';
const NUMERIC_TZ_LABEL = 'GMT+8';

const toSingaporeTime = (date: dayjs.ConfigType) => dayjs(date).tz(DEFAULT_TIMEZONE);

// Private helper: Natural date format with Singapore timezone
const formatDateNatural = (date: dayjs.ConfigType, precision: DatePrecisionType = DatePrecision.DATE_TIME_MINUTE) => {
  const value = toSingaporeTime(date);
  switch (precision) {
    case DatePrecision.DATE_ONLY:
      return value.format('D MMM YYYY');
    case DatePrecision.DATE_TIME_SECOND:
      return value.format(`D MMM YYYY [at] h:mm:ss A [${NATURAL_TZ_LABEL}]`);
    default:
      return value.format(`D MMM YYYY [at] h:mm A [${NATURAL_TZ_LABEL}]`);
  }
};

// Private helper: Numeric date format with Singapore timezone
const formatDateNumeric = (date: dayjs.ConfigType, precision: DatePrecisionType = DatePrecision.DATE_ONLY) => {
  const value = toSingaporeTime(date);
  switch (precision) {
    case DatePrecision.DATE_ONLY:
      return value.format('YYYY-MM-DD');
    case DatePrecision.DATE_TIME_SECOND:
      return value.format(`YYYY-MM-DD, h:mm:ss A [${NUMERIC_TZ_LABEL}]`);
    default:
      return value.format(`YYYY-MM-DD, h:mm A [${NUMERIC_TZ_LABEL}]`);
  }
};

// Main date formatting function that supports different languages and styles
// - style 'numeric': YYYY-MM-DD format with GMT+8 timezone label
// - style 'natural': "31 Jan 2025" format with SGT timezone label (English month names only)
// - precision: level of detail (date-only, date-time-minute, date-time-second)
// - language: locale for month names (only affects natural style)
// Note: Chinese ('zh') is forced to numeric style as natural format doesn't support Chinese month names
// Default style: numeric for Chinese ('zh'), natural for others
export const formatDate = (
  date: dayjs.ConfigType,
  language: string,
  style: DateStyleType = language === 'zh' ? DateStyle.NUMERIC : DateStyle.NATURAL,
  precision: DatePrecisionType = DatePrecision.DATE_ONLY
) => {
  // Force numeric style for Chinese as natural format doesn't support Chinese month names
  if (language === 'zh' || style === DateStyle.NUMERIC) {
    return formatDateNumeric(date, precision);
  }
  return formatDateNatural(date, precision);
};
